import { Box, CircleButton, PartnerAccountStatus, TextField, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Title } from '@/components/UI';
import { usePartnerData } from '@/providers/PartnerData.provider';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import styles from './ApplicationPendingState.module.scss';
import PendingActionsIcon from './pending_actions.svg';

const cn = withStyles(styles);

type ApplicationPendingStateProps = {
  /** The account's status, which the banner is titled with (ID-13964 D6); undefined until known. */
  status?: PartnerAccountStatus;
  onBoardingLink: string | null;
  hasValidOnboardingUrl: boolean | null;
  /** False when the account's owning partner emails its own accounts (Partner Emails Account): no send button. */
  isOnboardingEmailAllowed: boolean;
  onCopy: () => void;
  onSendEmail: () => void;
};

/**
 * What every tab shows while the account is still being onboarded: the status it is in, and the
 * onboarding link to share. The link and its buttons wrap onto their own lines when the tab is too
 * narrow to hold them side by side.
 */
export default function ApplicationPendingState({
  status,
  onBoardingLink,
  hasValidOnboardingUrl,
  isOnboardingEmailAllowed,
  onCopy,
  onSendEmail,
}: ApplicationPendingStateProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.ApplicationPending' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  return (
    <div className={cn('container')} data-testid="applicationPending">
      <img src={PendingActionsIcon} className={cn('icon')} alt="" />

      <Box flx="col" ai="center" gapSize="8px">
        {/* The current status by name, so the banner never contradicts the chip above it. */}
        <Title level="4" className={cn('title')} data-testid="applicationPendingTitle">
          {status ? resolveStatusLabel(status) : t('title', { defaultValue: 'Account activation pending' })}
        </Title>

        <p className={cn('description')}>
          {t('description', {
            defaultValue: 'The customer needs to complete their onboarding first. Share the link below to get started.',
          })}
        </p>
      </Box>

      <div className={cn('linkRow')}>
        <TextField
          inputClass={cn('readonlyInput')}
          containerWidth="auto"
          name="onboardingUrl"
          readOnly
          variant="outlineShadow"
          value={onBoardingLink || ''}
          ariaLabel={t('linkLabel', { defaultValue: 'Onboarding link' })}
        />

        <div className={cn('actions')}>
          <CircleButton
            colorScheme="action"
            icon="content_copy"
            aria-label={tGlobal('Buttons.copy')}
            onClick={onCopy}
            disabled={!hasValidOnboardingUrl || isRoleViewOnly}
          />

          {isOnboardingEmailAllowed && (
            <CircleButton
              colorScheme="action"
              icon="send"
              aria-label={tGlobal('Buttons.send')}
              onClick={onSendEmail}
              disabled={!hasValidOnboardingUrl || isRoleViewOnly}
            />
          )}
        </div>
      </div>
    </div>
  );
}
